import { useState, useEffect } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, User as UserIcon, Phone, Mail, Clock, Wrench, Users, Utensils,
  Truck, ListChecks, Download, Save, Coffee, Sparkles, Calendar as CalendarIcon,
  ChevronRight, ChevronDown, FileText, X, Bell, Send
} from "lucide-react";

// Helpers durée (ex : "3h30" ou "2:30" → minutes)
const parseDuration = (raw) => {
  if (!raw) return 0;
  const s = String(raw).trim().toLowerCase().replace(",", ".");
  let m = s.match(/^(\d+)\s*h\s*(\d{1,2})?$/);
  if (m) return parseInt(m[1]) * 60 + (parseInt(m[2] || "0"));
  m = s.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m) return parseInt(m[1]) * 60 + parseInt(m[2]);
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return Math.round(parseFloat(m[1]) * 60);
  return 0;
};
const addDuration = (hhmm, minutes) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(x => parseInt(x || "0"));
  const total = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const CATEGORIES = [
  { v: "montage",    label: "Montage",    color: "bg-primary/20 text-primary border-primary/40" },
  { v: "action",     label: "Action",     color: "bg-foreground/10 text-foreground border-foreground/40" },
  { v: "contact",    label: "Contact",    color: "bg-secondary text-secondary-foreground border-secondary" },
  { v: "repas",      label: "Repas",      color: "bg-accent text-accent-foreground border-accent" },
  { v: "demontage",  label: "Démontage",  color: "bg-purple-500/15 text-purple-200 border-purple-500/50" },
  { v: "autre",      label: "Autre",      color: "bg-muted text-muted-foreground border-border" },
];
const CATEGORY_STYLE = Object.fromEntries(CATEGORIES.map(c => [c.v, c.color]));
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.v, c.label]));

/** Panneau dépliable pour chaque prestataire/artiste validé.
 *  5 blocs : Documents, Acompte, Contact sur place, Repas, Véhicules (+ demande décla si artiste). */
function RequestExtrasPanel({ r, event, onSaved }) {
  const eventId = event.id;
  const ex = r.extras || {};
  const [documents, setDocuments] = useState(ex.documents || []);
  const [deposit, setDeposit] = useState(ex.deposit || { mode: "percent", percent: "", amount: "", status: "en_attente" });
  const [onsite, setOnsite] = useState(ex.onsite || { name: "", email: "", phone: "", arrival_time: "", setup_duration: "", comment: "", people: [] });
  const [meals, setMeals] = useState(ex.meals || { none: "", veggie: "", vegan: "", no_pork: "", other: "", other_detail: "" });
  const [vehicles, setVehicles] = useState(ex.vehicles || []);
  const [newVeh, setNewVeh] = useState({ vehicle_type: "", plate: "", driver: "", note: "" });
  const [newPerson, setNewPerson] = useState({ first_name: "", last_name: "" });
  const save = async (block, value) => {
    try {
      await api.patch(`/events/${eventId}/requests/${r.id}/extras?kind=${r.kind}`, { [block]: value });
      toast.success("Enregistré");
    } catch (e) { toast.error(extractError(e)); }
  };
  const uploadDocs = async (files) => {
    if (!files || !files.length) return;
    const stored = [];
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        stored.push({ id: data.id, name: f.name, size: data.size, uploaded_at: new Date().toISOString() });
      } catch (e) { toast.error("Upload échoué : " + f.name); }
    }
    const merged = [...documents, ...stored];
    setDocuments(merged);
    await save("documents", merged);
  };
  const removeDoc = async (id) => {
    const merged = documents.filter(d => d.id !== id);
    setDocuments(merged);
    await save("documents", merged);
  };
  const mealsTotal = ["none", "veggie", "vegan", "no_pork", "other"].reduce((s, k) => s + (parseInt(meals[k]) || 0), 0);
  const totalHT = Number(r.offer_amount || 0);
  const depositEur = deposit.mode === "percent"
    ? (totalHT * (parseFloat(deposit.percent) || 0) / 100)
    : (parseFloat(deposit.amount) || 0);
  return (
    <div className="mt-3 pt-3 border-t border-border/60 space-y-4 text-sm" data-testid={`req-extras-${r.id}`}>
      {/* Documents */}
      <div>
        <Label className="text-xs font-bold">Devis / Contrat signé (multi-fichiers)</Label>
        <input type="file" multiple onChange={e => uploadDocs(e.target.files)} className="block mt-1 text-xs" data-testid={`req-docs-input-${r.id}`} />
        {documents.length > 0 && (
          <div className="mt-1 space-y-1">
            {documents.map(d => (
              <div key={d.id} className="flex items-center gap-2 text-xs bg-card border rounded-md px-2 py-1">
                <FileText className="h-3 w-3" />
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.id}`} target="_blank" rel="noreferrer" className="flex-1 truncate underline text-primary">{d.name}</a>
                <button onClick={() => removeDoc(d.id)} className="text-destructive"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Acompte */}
      <div>
        <Label className="text-xs font-bold">Acompte</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
          <select value={deposit.mode} onChange={e => setDeposit({ ...deposit, mode: e.target.value })} className="h-8 rounded-md border px-2 text-xs" data-testid={`req-deposit-mode-${r.id}`}>
            <option value="percent">% du HT</option>
            <option value="amount">Montant € HT</option>
          </select>
          {deposit.mode === "percent" ? (
            <Input type="number" step="1" value={deposit.percent} onChange={e => setDeposit({ ...deposit, percent: e.target.value })} placeholder="%" className="h-8 text-xs" onBlur={() => save("deposit", deposit)} />
          ) : (
            <Input type="number" step="0.01" value={deposit.amount} onChange={e => setDeposit({ ...deposit, amount: e.target.value })} placeholder="€ HT" className="h-8 text-xs" onBlur={() => save("deposit", deposit)} />
          )}
          <div className="text-xs self-center tabular-nums font-semibold">= {depositEur.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
          <select value={deposit.status} onChange={e => { const d = { ...deposit, status: e.target.value }; setDeposit(d); save("deposit", d); }} className="h-8 rounded-md border px-2 text-xs" data-testid={`req-deposit-status-${r.id}`}>
            <option value="en_attente">En attente de facture</option>
            <option value="a_payer">À payer</option>
            <option value="paye">Payé ✓</option>
          </select>
        </div>
        {/* Upload facture d'acompte pour la comptabilité */}
        <div className="mt-2">
          <Label className="text-[10px]">Facture d'acompte (PDF)</Label>
          <input
            type="file"
            accept="application/pdf"
            multiple
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              const stored = [];
              for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                try {
                  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  stored.push({ id: data.id, name: f.name, size: data.size, uploaded_at: new Date().toISOString() });
                } catch (err) { toast.error("Upload échoué : " + f.name); }
              }
              const newDep = { ...deposit, invoice_files: [...(deposit.invoice_files || []), ...stored] };
              setDeposit(newDep); save("deposit", newDep);
            }}
            className="block mt-1 text-xs"
            data-testid={`req-deposit-invoice-input-${r.id}`}
          />
          {(deposit.invoice_files || []).length > 0 && (
            <div className="mt-1 space-y-1">
              {(deposit.invoice_files || []).map(f => (
                <div key={f.id} className="flex items-center gap-2 text-xs bg-card border rounded-md px-2 py-1" data-testid={`req-deposit-invoice-file-${f.id}`}>
                  <FileText className="h-3 w-3 text-primary" />
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}`} target="_blank" rel="noreferrer" className="flex-1 truncate underline text-primary">{f.name}</a>
                  <button onClick={() => {
                    const newDep = { ...deposit, invoice_files: (deposit.invoice_files || []).filter(x => x.id !== f.id) };
                    setDeposit(newDep); save("deposit", newDep);
                  }} className="text-destructive"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Contact sur place */}
      <div>
        <Label className="text-xs font-bold">Contact sur place & logistique</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
          <Input value={onsite.name} onChange={e => setOnsite({ ...onsite, name: e.target.value })} onBlur={() => save("onsite", onsite)} placeholder="Nom prénom" className="h-8 text-xs" />
          <Input type="email" value={onsite.email} onChange={e => setOnsite({ ...onsite, email: e.target.value })} onBlur={() => save("onsite", onsite)} placeholder="Email" className="h-8 text-xs" />
          <Input value={onsite.phone} onChange={e => setOnsite({ ...onsite, phone: e.target.value })} onBlur={() => save("onsite", onsite)} placeholder="Téléphone" className="h-8 text-xs" />
          <Input type="time" value={onsite.arrival_time} onChange={e => setOnsite({ ...onsite, arrival_time: e.target.value })} onBlur={() => save("onsite", onsite)} placeholder="Horaire arrivée" className="h-8 text-xs" />
          <Input value={onsite.setup_duration} onChange={e => setOnsite({ ...onsite, setup_duration: e.target.value })} onBlur={() => save("onsite", onsite)} placeholder="Durée montage" className="h-8 text-xs" />
          <div className="text-xs self-center">{(onsite.people || []).length} personne(s)</div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <Input value={newPerson.first_name} onChange={e => setNewPerson({ ...newPerson, first_name: e.target.value })} placeholder="Prénom" className="h-8 text-xs" />
          <Input value={newPerson.last_name} onChange={e => setNewPerson({ ...newPerson, last_name: e.target.value })} placeholder="Nom" className="h-8 text-xs" />
          <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => {
            if (!newPerson.first_name && !newPerson.last_name) return;
            const people = [...(onsite.people || []), { ...newPerson, id: Date.now().toString() }];
            const o = { ...onsite, people };
            setOnsite(o); setNewPerson({ first_name: "", last_name: "" }); save("onsite", o);
          }}><Plus className="h-3 w-3 mr-1" /> Ajouter personne</Button>
        </div>
        {(onsite.people || []).length > 0 && (
          <div className="mt-1 space-y-1">
            {onsite.people.map(p => (
              <div key={p.id} className="flex items-center gap-2 text-xs bg-card border rounded-md px-2 py-1">
                <span className="flex-1">{p.first_name} {p.last_name}</span>
                <button onClick={() => { const o = { ...onsite, people: onsite.people.filter(x => x.id !== p.id) }; setOnsite(o); save("onsite", o); }} className="text-destructive"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
        <Textarea value={onsite.comment} onChange={e => setOnsite({ ...onsite, comment: e.target.value })} onBlur={() => save("onsite", onsite)} placeholder="Commentaire" rows={2} className="text-xs mt-2" />
      </div>
      {/* Repas */}
      <div>
        <Label className="text-xs font-bold">Repas · Total auto : <span className="text-primary">{mealsTotal}</span></Label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-1">
          {[["none", "Sans régime"], ["veggie", "Végétarien"], ["vegan", "Vegan"], ["no_pork", "Sans porc"], ["other", "Autre"]].map(([k, lbl]) => (
            <div key={k}>
              <Label className="text-[10px]">{lbl}</Label>
              <Input type="number" min="0" value={meals[k]} onChange={e => setMeals({ ...meals, [k]: e.target.value })} onBlur={() => save("meals", meals)} className="h-8 text-xs" />
            </div>
          ))}
        </div>
        {(parseInt(meals.other) || 0) > 0 && (
          <Input value={meals.other_detail} onChange={e => setMeals({ ...meals, other_detail: e.target.value })} onBlur={() => save("meals", meals)} placeholder="Préciser (allergies, sans gluten…)" className="mt-2 h-8 text-xs" />
        )}
      </div>
      {/* Véhicules */}
      <div>
        <Label className="text-xs font-bold">Véhicules ({vehicles.length})</Label>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-1 items-end">
          <Input value={newVeh.vehicle_type} onChange={e => setNewVeh({ ...newVeh, vehicle_type: e.target.value })} placeholder="Type (Camion 20m3)" className="h-8 text-xs" />
          <Input value={newVeh.plate} onChange={e => setNewVeh({ ...newVeh, plate: e.target.value })} placeholder="Plaque" className="h-8 text-xs" />
          <Input value={newVeh.driver} onChange={e => setNewVeh({ ...newVeh, driver: e.target.value })} placeholder="Chauffeur" className="h-8 text-xs" />
          <Input value={newVeh.note} onChange={e => setNewVeh({ ...newVeh, note: e.target.value })} placeholder="Note" className="h-8 text-xs" />
          <Button size="sm" className="h-8 rounded-full bg-primary" onClick={() => {
            if (!newVeh.vehicle_type && !newVeh.plate) return;
            const list = [...vehicles, { ...newVeh, id: Date.now().toString() }];
            setVehicles(list); setNewVeh({ vehicle_type: "", plate: "", driver: "", note: "" }); save("vehicles", list);
          }}><Plus className="h-3 w-3 mr-1" /> Ajouter</Button>
        </div>
        {vehicles.length > 0 && (
          <div className="mt-1 space-y-1">
            {vehicles.map(v => (
              <div key={v.id} className="flex items-center gap-2 text-xs bg-card border rounded-md px-2 py-1">
                <span className="flex-1">{v.vehicle_type} · {v.plate} · {v.driver} {v.note && `· ${v.note}`}</span>
                <button onClick={() => { const list = vehicles.filter(x => x.id !== v.id); setVehicles(list); save("vehicles", list); }} className="text-destructive"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Artiste : demande de déclaration */}
      {r.kind === "artist" && (
        <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 p-3 space-y-2">
          <Label className="text-xs font-bold">Déclarations intermittent (artiste + accompagnants)</Label>
          {(() => {
            // Liste des déclarations liées à cet événement dont l'intermittent porte le même nom
            // que l'artiste (matching approximatif : first_name+last_name / stage_name ↔ r.name)
            const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
            const target = norm(r.name);
            const linked = (event.linked_declarations || []).filter(d => {
              const im = d.intermittent || {};
              const full = norm(`${im.first_name || ""} ${im.last_name || ""}`);
              const stage = norm(im.stage_name);
              return target && (target.includes(full) || (full && full.includes(target)) || target === stage || target.includes(stage) || (stage && stage.includes(target)));
            });
            return (
              <>
                <div className="text-[11px] text-muted-foreground">
                  Créez une déclaration pré-remplie pour l'artiste, puis ajoutez-en d'autres pour les musiciens/techniciens accompagnants.
                </div>
                {linked.length > 0 && (
                  <div className="rounded-md border border-primary/30 bg-card p-2 space-y-1" data-testid={`req-artist-decl-linked-${r.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-primary font-bold">{linked.length} déclaration(s) déjà liée(s)</div>
                    {linked.map(d => (
                      <a key={d.id} href={`/declarations`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-md border border-border/50 bg-muted px-2 py-1 text-[11px] hover:bg-muted transition">
                        <Badge className="rounded-full text-[9px] bg-primary/20 text-primary border-primary/40">{d.status || "contrat_a_faire"}</Badge>
                        <span className="flex-1 truncate">{(d.intermittent?.first_name || "")} {d.intermittent?.last_name || d.intermittent?.stage_name || ""} · <span className="text-muted-foreground">{d.role || "—"}</span></span>
                        <span className="tabular-nums text-muted-foreground">{d.start_date || d.period}</span>
                        <span className="tabular-nums font-semibold text-primary">{Number(d.cost_ht_client || 0).toLocaleString("fr-FR")} €</span>
                      </a>
                    ))}
                  </div>
                )}
                {(() => {
                  const notesParts = [];
                  if (r.payment_mode === "declaration") {
                    // Nouveau : plusieurs déclarations possibles ; fallback sur les
                    // champs scalaires legacy si l'array n'existe pas.
                    const decls = (Array.isArray(r.declarations) && r.declarations.length > 0)
                      ? r.declarations
                      : ((r.cachet_amount != null || (r.frais_amount != null && r.frais_amount > 0))
                          ? [{ cachet_type: r.cachet_type, cachet_amount: r.cachet_amount, frais_amount: r.frais_amount }]
                          : []);
                    decls.forEach((d, i) => {
                      const prefix = decls.length > 1 ? `#${i + 1} ` : "";
                      if (d.cachet_amount != null) {
                        notesParts.push(`${prefix}Cachet ${d.cachet_type || "net"} : ${Number(d.cachet_amount).toLocaleString("fr-FR")} €`);
                      }
                      if (d.frais_amount != null && d.frais_amount > 0) {
                        notesParts.push(`${prefix}Frais : ${Number(d.frais_amount).toLocaleString("fr-FR")} €`);
                      }
                    });
                  }
                  if (r.offer_comment) notesParts.push(r.offer_comment);
                  const prefillComment = notesParts.join(" · ");
                  const url = `/declarations?prefill_event=${encodeURIComponent(eventId)}&prefill_event_name=${encodeURIComponent(event.name || "")}&prefill_date=${encodeURIComponent(event.start_date || "")}&prefill_name=${encodeURIComponent(r.name)}&prefill_role=${encodeURIComponent(r.category || "Artiste")}&prefill_role_type=artiste${prefillComment ? `&prefill_comment=${encodeURIComponent(prefillComment)}` : ""}`;
                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5"
                      data-testid={`req-artist-decl-${r.id}`}
                    >
                      <Plus className="h-3 w-3" /> {linked.length > 0 ? "Ajouter une autre déclaration" : "Créer une déclaration pré-remplie"}
                    </a>
                  );
                })()}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

/** Sélecteur AAE réutilisable pour "Chargé de commercialisation" / "Chargé de projet".
 *  Persiste immédiatement via PATCH /events/{id}. */
export function LeadSelector({ event, reload, field, currentName, label, subtitle, testId }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users/lookup").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const val = event[field] || "";
  const save = async (uid) => {
    try {
      await api.patch(`/events/${event.id}`, { [field]: uid || null });
      toast.success(`${label} enregistré`);
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  return (
    <Card className="border-primary/30 bg-primary/5 shadow-warm" data-testid={`${testId}-card`}>
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
          <div className="font-brand text-lg mt-0.5" data-testid={`${testId}-name`}>{currentName || <span className="text-muted-foreground italic text-sm">Non désigné</span>}</div>
          {subtitle && <div className="text-[11px] text-muted-foreground mt-1">{subtitle}</div>}
        </div>
        <select
          value={val}
          onChange={e => save(e.target.value)}
          className="rounded-full border border-input bg-card px-3 py-2 text-sm min-w-[220px]"
          data-testid={`${testId}-select`}
        >
          <option value="">— Désigner —</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
      </CardContent>
    </Card>
  );
}

function EmptyProduction() {
  return {
    // Nouveau (Équipe AAE / Client / Lieu / Prestataires)
    team_lead_id: null, team_lead_arrival: "", team_lead_vehicle: "",
    team_members: [],
    client_onsite: { name: "", first_name: "", phone: "", email: "", role: "", arrival_time: "", meal: false, comment: "" },
    venue_onsite:  { name: "", first_name: "", phone: "", email: "", role: "", arrival_time: "", comment: "" },
    providers_onsite: [],
    // Legacy
    onsite_contact_name: "", onsite_contact_email: "", onsite_contact_phone: "",
    arrival_time: "", setup_duration: "", people_count: 0, comment: "",
    meals_count: 0, meals_veggie: 0, meals_vegan: 0, meals_no_pork: 0,
    vehicles: [], roadmap: [],
  };
}

// Bloc "contact sur place" réutilisable (client / lieu / prestataire)
function OnsiteContactBlock({ value, onChange, withMeal = true, testPrefix }) {
  const v = value || {};
  const setF = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div><Label className="text-xs">Prénom</Label><Input value={v.first_name || ""} onChange={e => setF("first_name", e.target.value)} data-testid={`${testPrefix}-first-name`} /></div>
      <div><Label className="text-xs">Nom</Label><Input value={v.name || ""} onChange={e => setF("name", e.target.value)} data-testid={`${testPrefix}-name`} /></div>
      <div><Label className="text-xs">Téléphone</Label><Input value={v.phone || ""} onChange={e => setF("phone", e.target.value)} /></div>
      <div><Label className="text-xs">Email</Label><Input value={v.email || ""} onChange={e => setF("email", e.target.value)} /></div>
      <div><Label className="text-xs">Poste</Label><Input value={v.role || ""} onChange={e => setF("role", e.target.value)} /></div>
      <div><Label className="text-xs">Horaire d'arrivée sur place</Label><Input type="time" value={v.arrival_time || ""} onChange={e => setF("arrival_time", e.target.value)} /></div>
      {withMeal && (
        <div className="md:col-span-2 flex items-center gap-2">
          <Checkbox checked={!!v.meal} onCheckedChange={c => setF("meal", !!c)} id={`${testPrefix}-meal`} />
          <Label htmlFor={`${testPrefix}-meal`} className="text-sm">Repas prévu</Label>
        </div>
      )}
      <div className="md:col-span-2"><Label className="text-xs">Commentaire</Label>
        <Textarea rows={2} value={v.comment || ""} onChange={e => setF("comment", e.target.value)} />
      </div>
    </div>
  );
}

// Sélecteur de véhicule (Camion AAE / Véhicule perso)
const VEHICLE_OPTS = [
  { v: "camion_aae", label: "Camion AAE" },
  { v: "vehicule_perso", label: "Véhicule perso" },
];

export default function EventProductionTab({ event, reload }) {
  const { user } = useAuth();
  const isAdminOrManager = user?.role === "admin" || user?.role === "manager";
  const [prod, setProd] = useState({ ...EmptyProduction(), ...(event.production || {}) });
  const set = (patch) => setProd(p => ({ ...p, ...patch }));
  const [teamUsers, setTeamUsers] = useState([]);
  const [notifOverrides, setNotifOverrides] = useState({}); // {reqId: {validation_date, validation_channel}}
  const [expandedReq, setExpandedReq] = useState(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);

  const openReminderDialog = async () => {
    setReminderOpen(true);
    setReminderLoading(true);
    setReminderPreview(null);
    try {
      const { data } = await api.get(`/events/${event.id}/day-before-reminder-preview`);
      setReminderPreview(data);
    } catch (e) {
      toast.error(extractError(e));
      setReminderOpen(false);
    } finally {
      setReminderLoading(false);
    }
  };
  const sendReminderNow = async () => {
    setReminderSending(true);
    try {
      const { data } = await api.post(`/events/${event.id}/send-day-before-reminder`);
      toast.success(`Récap J-1 envoyé à ${data.sent} destinataire(s)${data.failed ? " · " + data.failed + " échec(s)" : ""}`);
      setReminderOpen(false);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setReminderSending(false);
    }
  };

  useEffect(() => {
    setProd({ ...EmptyProduction(), ...(event.production || {}) });
    // eslint-disable-next-line
  }, [event.id]);
  useEffect(() => { api.get("/users/lookup").then(({ data }) => setTeamUsers(data)).catch(() => {}); }, []);

  // Contacts du client entreprise (pour import rapide dans "Client — contact sur place")
  const [clientContacts, setClientContacts] = useState([]);
  useEffect(() => {
    if (!event.client) { setClientContacts([]); return; }
    api.get("/clients").then(({ data }) => {
      const match = (data || []).find(c => (c.name || "").trim().toLowerCase() === (event.client || "").trim().toLowerCase());
      setClientContacts(match?.contacts || []);
    }).catch(() => setClientContacts([]));
  }, [event.client]);
  const importClientContact = (idx) => {
    const c = clientContacts[idx];
    if (!c) return;
    set({ client_onsite: {
      ...(prod.client_onsite || {}),
      first_name: c.first_name || "",
      name: c.last_name || "",
      phone: c.phone || "",
      email: c.email || "",
      role: c.role || "",
    }});
    toast.success("Contact importé — pensez à enregistrer");
  };

  // Import contact Lieu depuis les fiches Prestataires/Artistes validés déjà présents sur l'événement
  const [providerContacts, setProviderContacts] = useState([]);
  useEffect(() => {
    const ids = validatedRequests.map(r => r.contact_id).filter(Boolean);
    if (!ids.length) { setProviderContacts([]); return; }
    Promise.all(ids.map(id => api.get(`/contacts/${id}`).then(r => r.data).catch(() => null)))
      .then(list => setProviderContacts(list.filter(Boolean)));
  // eslint-disable-next-line
  }, [event.id, event.provider_requests, event.artist_requests]);
  const importVenueContact = (idx) => {
    const c = providerContacts[idx];
    if (!c) return;
    set({ venue_onsite: {
      ...(prod.venue_onsite || {}),
      first_name: c.first_name || "",
      name: c.last_name || c.stage_name || c.company || "",
      phone: c.phone || "",
      email: c.email || "",
      role: c.category || (c.kind === "artist" ? "Artiste" : "Prestataire"),
    }});
    toast.success("Contact importé — pensez à enregistrer");
  };

  // Prestataires + artistes validés (offer_decision=validated) → à afficher en Production
  const validatedRequests = [
    ...(event.provider_requests || []).filter(r => r.offer_decision === "validated").map(r => ({ ...r, kind: "provider" })),
    ...(event.artist_requests   || []).filter(r => r.offer_decision === "validated").map(r => ({ ...r, kind: "artist" })),
  ];
  const providerOnsite = (rid) => (prod.providers_onsite || []).find(p => p.request_id === rid) || {};
  const updateProviderOnsite = (rid, kind, patch) => {
    const list = [...(prod.providers_onsite || [])];
    const idx = list.findIndex(p => p.request_id === rid);
    if (idx >= 0) list[idx] = { ...list[idx], ...patch };
    else list.push({ request_id: rid, request_kind: kind, ...patch });
    set({ providers_onsite: list });
  };
  // Export Google Calendar : ouvre calendar.google.com avec un événement pré-rempli
  const openGoogleCalendar = () => {
    const pad = n => String(n).padStart(2, "0");
    const fmt = (dateStr, timeStr, fallbackHour) => {
      if (!dateStr) return "";
      const t = (timeStr || `${pad(fallbackHour)}:00`).replace(":", "") + "00";
      return dateStr.replace(/-/g, "") + "T" + t;
    };
    // Trouver l'horaire d'arrivée le + tôt côté équipe/prestas pour DTSTART
    const times = [prod.team_lead_arrival, ...(prod.team_members || []).map(m => m.arrival_time),
                   prod.client_onsite?.arrival_time, prod.venue_onsite?.arrival_time,
                   ...(prod.providers_onsite || []).map(p => p.arrival_time)]
                  .filter(Boolean).sort();
    const startTime = times[0] || "09:00";
    const endTime = "23:00";
    const startDate = event.start_date;
    const endDate = event.end_date || event.start_date;
    if (!startDate) { toast.error("Date de début manquante sur l'événement"); return; }
    const dtStart = fmt(startDate, startTime, 9);
    const dtEnd = fmt(endDate, endTime, 23);

    const line = (label, obj) => obj && (obj.name || obj.first_name || obj.email || obj.phone)
      ? `• ${label} : ${[obj.first_name, obj.name].filter(Boolean).join(" ")}${obj.role ? " (" + obj.role + ")" : ""}${obj.arrival_time ? " — arrivée " + obj.arrival_time : ""}${obj.phone ? " · " + obj.phone : ""}${obj.email ? " · " + obj.email : ""}`
      : null;
    const teamLeadUser = teamUsers.find(u => u.id === prod.team_lead_id);
    const details = [
      `Événement : ${event.name}`,
      event.client ? `Client : ${event.client}` : null,
      "",
      "— ÉQUIPE AAE —",
      teamLeadUser ? `• Chargé de projet : ${teamLeadUser.name || teamLeadUser.email}${prod.team_lead_arrival ? " — arrivée " + prod.team_lead_arrival : ""}${prod.team_lead_vehicle ? " · " + (prod.team_lead_vehicle === "camion_aae" ? "Camion AAE" : "Véhicule perso") : ""}` : null,
      ...(prod.team_members || []).map(m => `• ${m.name}${m.arrival_time ? " — arrivée " + m.arrival_time : ""}${m.vehicle ? " · " + (m.vehicle === "camion_aae" ? "Camion AAE" : "Véhicule perso") : ""}`),
      "",
      "— CLIENT —",
      line("Contact client", prod.client_onsite),
      "",
      "— LIEU —",
      line("Contact lieu", prod.venue_onsite),
      "",
      "— PRESTATAIRES & ARTISTES VALIDÉS —",
      ...validatedRequests.map(r => {
        const po = (prod.providers_onsite || []).find(p => p.request_id === r.id) || {};
        return `• ${r.name}${r.category ? " (" + r.category + ")" : ""}${po.onsite_name ? " — " + po.onsite_name : ""}${po.arrival_time ? " — arrivée " + po.arrival_time : ""}${po.onsite_phone ? " · " + po.onsite_phone : ""}${po.vehicle ? " · " + po.vehicle : ""}`;
      }),
    ].filter(Boolean).join("\n");

    // Emails des contacts sur place pour ajout automatique en tant qu'invités
    const attendees = [
      prod.client_onsite?.email,
      prod.venue_onsite?.email,
      ...(prod.providers_onsite || []).map(p => p.onsite_email),
    ].filter(e => e && /@/.test(e));

    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: `${event.name} — Production`,
      dates: `${dtStart}/${dtEnd}`,
      details,
      location: event.address || event.location || "",
    });
    if (attendees.length) params.append("add", attendees.join(","));
    window.open("https://calendar.google.com/calendar/render?" + params.toString(), "_blank", "noopener");
    toast.success("Événement Google Calendar prêt à être enregistré");
  };

  // Export .ics — compatible Apple Calendar, Outlook, Thunderbird, etc.
  const downloadIcs = () => {
    const pad = n => String(n).padStart(2, "0");
    const fmt = (dateStr, timeStr, fallbackHour) => {
      if (!dateStr) return "";
      const t = (timeStr || `${pad(fallbackHour)}:00`).replace(":", "") + "00";
      return dateStr.replace(/-/g, "") + "T" + t;
    };
    const times = [prod.team_lead_arrival, ...(prod.team_members || []).map(m => m.arrival_time),
                   prod.client_onsite?.arrival_time, prod.venue_onsite?.arrival_time,
                   ...(prod.providers_onsite || []).map(p => p.arrival_time)]
                  .filter(Boolean).sort();
    const startTime = times[0] || "09:00";
    const startDate = event.start_date;
    const endDate = event.end_date || event.start_date;
    if (!startDate) { toast.error("Date de début manquante sur l'événement"); return; }
    const dtStart = fmt(startDate, startTime, 9);
    const dtEnd = fmt(endDate, "23:00", 23);
    const now = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

    const line = (label, obj) => obj && (obj.name || obj.first_name || obj.email || obj.phone)
      ? `- ${label} : ${[obj.first_name, obj.name].filter(Boolean).join(" ")}${obj.role ? " (" + obj.role + ")" : ""}${obj.arrival_time ? " — arrivée " + obj.arrival_time : ""}${obj.phone ? " · " + obj.phone : ""}${obj.email ? " · " + obj.email : ""}`
      : null;
    const teamLeadUser = teamUsers.find(u => u.id === prod.team_lead_id);
    const descLines = [
      `Événement : ${event.name}`,
      event.client ? `Client : ${event.client}` : null,
      "",
      "-- ÉQUIPE AAE --",
      teamLeadUser ? `- Chargé de projet : ${teamLeadUser.name || teamLeadUser.email}${prod.team_lead_arrival ? " — arrivée " + prod.team_lead_arrival : ""}${prod.team_lead_vehicle ? " · " + (prod.team_lead_vehicle === "camion_aae" ? "Camion AAE" : "Véhicule perso") : ""}` : null,
      ...(prod.team_members || []).map(m => `- ${m.name}${m.arrival_time ? " — arrivée " + m.arrival_time : ""}${m.vehicle ? " · " + (m.vehicle === "camion_aae" ? "Camion AAE" : "Véhicule perso") : ""}`),
      "",
      "-- CLIENT --",
      line("Contact client", prod.client_onsite),
      "",
      "-- LIEU --",
      line("Contact lieu", prod.venue_onsite),
      "",
      "-- PRESTATAIRES & ARTISTES VALIDÉS --",
      ...validatedRequests.map(r => {
        const po = (prod.providers_onsite || []).find(p => p.request_id === r.id) || {};
        return `- ${r.name}${r.category ? " (" + r.category + ")" : ""}${po.onsite_name ? " — " + po.onsite_name : ""}${po.arrival_time ? " — arrivée " + po.arrival_time : ""}${po.onsite_phone ? " · " + po.onsite_phone : ""}${po.vehicle ? " · " + po.vehicle : ""}`;
      }),
    ].filter(Boolean);

    // Échappement ICS (RFC5545) — virgules, points-virgules, backslash, nouvelles lignes
    const esc = s => String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
    const location = event.address || event.location || "";
    const attendees = [
      { email: prod.client_onsite?.email, name: [prod.client_onsite?.first_name, prod.client_onsite?.name].filter(Boolean).join(" ") || "Contact client" },
      { email: prod.venue_onsite?.email,  name: [prod.venue_onsite?.first_name,  prod.venue_onsite?.name].filter(Boolean).join(" ")  || "Contact lieu" },
      ...(prod.providers_onsite || []).map(p => ({ email: p.onsite_email, name: p.onsite_name || "Contact prestataire" })),
    ].filter(a => a.email && /@/.test(a.email));

    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//AAE Compagnon//Production Export//FR",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${event.id}@compagnon.antoineevenements.fr`,
      `DTSTAMP:${now}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${esc(event.name)} — Production`,
      `LOCATION:${esc(location)}`,
      `DESCRIPTION:${esc(descLines.join("\n"))}`,
      ...attendees.map(a => `ATTENDEE;CN=${esc(a.name)};RSVP=TRUE:mailto:${a.email}`),
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `production-${(event.name || "event").replace(/[^\w\-]+/g, "_")}.ics`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Fichier .ics téléchargé");
  };

  const saveAll = async () => {
    try {
      const payload = {
        ...prod,
        people_count: Number(prod.people_count) || 0,
        meals_count: Number(prod.meals_count) || 0,
        meals_veggie: Number(prod.meals_veggie) || 0,
        meals_vegan: Number(prod.meals_vegan) || 0,
        meals_no_pork: Number(prod.meals_no_pork) || 0,
        vehicles: [], roadmap: [],
      };
      await api.patch(`/events/${event.id}/production`, payload);
      toast.success("Production enregistrée");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  const [newVehicle, setNewVehicle] = useState({ vehicle_type: "", plate: "", driver: "", note: "" });
  const [newItem, setNewItem] = useState({ time_from: "", time_to: "", category: "montage", action: "", comment: "", who: "" });

  const saveMain = async () => {
    try {
      const payload = {
        ...prod,
        people_count: Number(prod.people_count) || 0,
        meals_count: Number(prod.meals_count) || 0,
        meals_veggie: Number(prod.meals_veggie) || 0,
        meals_vegan: Number(prod.meals_vegan) || 0,
        meals_no_pork: Number(prod.meals_no_pork) || 0,
        // On ne touche pas aux arrays via ce endpoint (le backend les préserve)
        vehicles: [],
        roadmap: [],
      };
      await api.patch(`/events/${event.id}/production`, payload);
      toast.success("Infos production enregistrées");
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const addVehicle = async () => {
    if (!newVehicle.vehicle_type.trim() && !newVehicle.plate.trim()) { toast.error("Type ou plaque obligatoire"); return; }
    try {
      await api.post(`/events/${event.id}/production/vehicles`, newVehicle);
      toast.success("Véhicule ajouté");
      setNewVehicle({ vehicle_type: "", plate: "", driver: "", note: "" });
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  const removeVehicle = async (v) => {
    if (!confirm(`Retirer ${v.vehicle_type || v.plate} ?`)) return;
    try { await api.delete(`/events/${event.id}/production/vehicles/${v.id}`); await reload(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const addItem = async () => {
    if (!newItem.time_from || !newItem.action.trim()) { toast.error("Heure et action obligatoires"); return; }
    try {
      await api.post(`/events/${event.id}/production/roadmap`, newItem);
      toast.success("Ligne ajoutée à la feuille de route");
      setNewItem({ time_from: "", time_to: "", category: newItem.category, action: "", comment: "", who: "" });
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  const removeItem = async (r) => {
    if (!confirm("Supprimer cette ligne ?")) return;
    try { await api.delete(`/events/${event.id}/production/roadmap/${r.id}`); await reload(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/events/${event.id}/production/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `FDR_${(event.name || "evenement").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Impossible de générer le PDF"); }
  };

  const prefillRoadmap = async () => {
    if (!prod.arrival_time) { toast.error("Renseignez l'horaire d'arrivée avant de préremplir"); return; }
    const dur = parseDuration(prod.setup_duration);
    if (!dur) { toast.error("Durée du montage invalide (ex : 3h, 3h30, 2:30)"); return; }
    const end = addDuration(prod.arrival_time, dur);
    // Évite les doublons si déjà présents
    const already = (prod.roadmap || []).some(r => r.action === "Arrivée équipe" || r.action === "Fin montage");
    if (already && !confirm("Des créneaux « Arrivée équipe » ou « Fin montage » existent déjà. En ajouter quand même ?")) return;
    try {
      await api.post(`/events/${event.id}/production/roadmap`, {
        time_from: prod.arrival_time,
        time_to: prod.arrival_time,
        category: "montage",
        action: "Arrivée équipe",
        who: "Équipe technique",
        comment: "",
      });
      await api.post(`/events/${event.id}/production/roadmap`, {
        time_from: end,
        time_to: end,
        category: "montage",
        action: "Fin montage",
        who: "Équipe technique",
        comment: `Durée totale : ${prod.setup_duration}`,
      });
      toast.success(`Roadmap préremplie : Arrivée ${prod.arrival_time} → Fin montage ${end}`);
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const roadmapSorted = [...(prod.roadmap || [])].sort((a, b) => (a.time_from || "").localeCompare(b.time_from || ""));

  const setCheckpoint = async (key, done) => {
    try {
      const { data } = await api.patch(`/events/${event.id}/checkpoint`, { key, done });
      if (data?.phase && data.phase !== event.phase) {
        toast.success("FDR envoyée — passage automatique en Post-production");
      }
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  const fdrPret = !!(event.checkpoints?.fdr_pret?.done);
  const fdrEnvoyee = !!(event.checkpoints?.fdr_envoyee?.done);

  return (
    <div className="space-y-6" data-testid="production-tab">
      {/* Chargé de projet — remplace le chargé de commercialisation en Production */}
      <LeadSelector
        event={event}
        reload={reload}
        field="project_lead_id"
        currentName={event.project_lead_name}
        label="Chargé de projet"
        subtitle="Membre AAE responsable de la production jusqu'à la clôture. Prend le relais du chargé de commercialisation."
        testId="project-lead"
      />

      {/* ==== Prestataires & Artistes validés (récap depuis Commercialisation) ==== */}
      {validatedRequests.length > 0 && (
        <Card className="border-emerald-400/40 shadow-warm" data-testid="prod-validated-card">
          <CardContent className="p-5 space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-emerald-600" /> Prestataires &amp; Artistes validés
            </div>
            <div className="rounded-lg border-2 border-amber-400 bg-amber-500/15 px-3 py-2 text-xs text-amber-200 font-semibold" data-testid="prod-validated-warning">
              ⚠️ Une confirmation écrite (SMS ou mail) est <strong>obligatoire</strong> de la part du chargé de projet auprès de chaque prestataire/artiste validé. Renseignez ci-dessous la date et le canal de cette confirmation.
            </div>
            {(() => {
              // Groupage : kind → category
              const groups = { provider: {}, artist: {} };
              validatedRequests.forEach(r => {
                const cat = r.category || "Autres";
                (groups[r.kind][cat] = groups[r.kind][cat] || []).push(r);
              });
              const kindLabel = { provider: "Prestataires", artist: "Artistes" };
              const saveNotif = async (r, patch) => {
                // Optimistic local update pour éviter le scroll-up (pas de reload)
                setNotifOverrides(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), ...patch } }));
                try {
                  await api.patch(`/events/${event.id}/requests/${r.id}/notification?kind=${r.kind}`, patch);
                } catch (e) {
                  toast.error(extractError(e));
                  await reload();
                }
              };
              // Applique les overrides locaux sur les requests pour l'affichage
              const applyOverride = (r) => ({ ...r, ...(notifOverrides[r.id] || {}) });
              return (
                <div className="space-y-4">
                  {["provider", "artist"].map(kind => {
                    const cats = Object.keys(groups[kind]).sort();
                    if (!cats.length) return null;
                    return (
                      <div key={kind} className="space-y-2">
                        <div className="font-brand text-lg text-primary">{kindLabel[kind]}</div>
                        {cats.map(cat => (
                          <div key={cat}>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1 pl-1">{cat}</div>
                            <div className="space-y-1.5">
                              {groups[kind][cat].map(raw => {
                                const r = applyOverride(raw);
                                const ok = !!(r.validation_date && r.validation_channel);
                                return (
                                  <div key={r.id} className={`rounded-lg border-2 p-3 ${ok ? "border-emerald-500 bg-emerald-500/15" : "border-red-400 bg-red-500/15"}`} data-testid={`prod-validated-row-${r.id}`}>
                                    <div className="flex flex-wrap gap-3 items-center cursor-pointer" onClick={() => setExpandedReq(prev => prev === r.id ? null : r.id)}>
                                      <div className="flex-1 min-w-[180px]">
                                        <div className="font-medium flex items-center gap-2">
                                          {expandedReq === r.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                          {r.name}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground">
                                          {Number(r.offer_amount || 0).toLocaleString("fr-FR")} € HT · Validée le {r.offer_decision_at || "—"}
                                        </div>
                                      </div>
                                      <div onClick={e => e.stopPropagation()}>
                                        <Label className="text-[10px]">Date confirmation *</Label>
                                        <Input
                                          type="date"
                                          value={r.validation_date || ""}
                                          onChange={e => saveNotif(r, { validation_date: e.target.value })}
                                          className="h-8 text-xs w-[150px]"
                                          data-testid={`prod-validation-date-${r.id}`}
                                        />
                                      </div>
                                      <div onClick={e => e.stopPropagation()}>
                                        <Label className="text-[10px]">Canal *</Label>
                                        <select
                                          value={r.validation_channel || ""}
                                          onChange={e => saveNotif(r, { validation_channel: e.target.value })}
                                          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                                          data-testid={`prod-validation-channel-${r.id}`}
                                        >
                                          <option value="">—</option>
                                          <option value="sms">SMS</option>
                                          <option value="mail">Mail</option>
                                        </select>
                                      </div>
                                      <span className={`text-[10px] font-bold uppercase tracking-wider ${ok ? "text-emerald-300" : "text-red-300"}`}>
                                        {ok ? "Confirmé" : "À confirmer"}
                                      </span>
                                    </div>
                                    {expandedReq === r.id && (
                                      <RequestExtrasPanel r={r} event={event} onSaved={reload} />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ==== Équipe AAE ==== */}
      <Card className="border-primary/30 shadow-warm" data-testid="prod-team-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <Users className="h-3 w-3 text-primary" /> Équipe AAE
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full border-primary/40 text-primary hover:bg-primary/5"
                onClick={openGoogleCalendar}
                data-testid="prod-gcal-btn"
              >
                <CalendarIcon className="h-3.5 w-3.5 mr-1" /> Google Calendar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={downloadIcs}
                data-testid="prod-ics-btn"
                title="Télécharge un fichier .ics compatible Apple Calendar, Outlook, Thunderbird…"
              >
                <Download className="h-3.5 w-3.5 mr-1" /> .ics
              </Button>
              {isAdminOrManager && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-amber-400 text-amber-200 hover:bg-amber-500/15"
                  onClick={openReminderDialog}
                  data-testid="prod-reminder-btn"
                  title="Envoyer un récap J-1 (horaires + adresses) à l'équipe AAE, prestataires, artistes et intermittents"
                >
                  <Bell className="h-3.5 w-3.5 mr-1" /> Rappel J-1
                </Button>
              )}
              <Button size="sm" className="rounded-full bg-primary" onClick={saveAll} data-testid="prod-save-all">
                <Save className="h-3.5 w-3.5 mr-1" /> Enregistrer la production
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Chargé de projet</Label>
              <select value={prod.team_lead_id || ""} onChange={e => set({ team_lead_id: e.target.value || null })}
                className="w-full h-10 rounded-md border border-border bg-background px-2 text-sm" data-testid="prod-team-lead">
                <option value="">— Choisir —</option>
                {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Horaire d'arrivée sur place</Label>
              <Input type="time" value={prod.team_lead_arrival || ""} onChange={e => set({ team_lead_arrival: e.target.value })} data-testid="prod-team-arrival" />
            </div>
            <div>
              <Label className="text-xs">Véhicule utilisé</Label>
              <select value={prod.team_lead_vehicle || ""} onChange={e => set({ team_lead_vehicle: e.target.value })}
                className="w-full h-10 rounded-md border border-border bg-background px-2 text-sm">
                <option value="">— Choisir —</option>
                {VEHICLE_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Autres personnes de l'équipe</div>
            <div className="space-y-2">
              {(prod.team_members || []).map((m, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid={`prod-tm-${i}`}>
                  <select value={m.user_id || ""} onChange={e => {
                    const uid = e.target.value;
                    const u = teamUsers.find(x => x.id === uid);
                    const l = [...prod.team_members]; l[i] = { ...l[i], user_id: uid, name: u ? (u.name || u.email) : "" }; set({ team_members: l });
                  }} className="h-10 rounded-md border border-border bg-background px-2 text-sm" data-testid={`prod-tm-user-${i}`}>
                    <option value="">— Choisir un membre AAE —</option>
                    {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                  <Input type="time" value={m.arrival_time || ""} onChange={e => { const l = [...prod.team_members]; l[i] = { ...l[i], arrival_time: e.target.value }; set({ team_members: l }); }} />
                  <select value={m.vehicle || ""} onChange={e => { const l = [...prod.team_members]; l[i] = { ...l[i], vehicle: e.target.value }; set({ team_members: l }); }}
                    className="h-10 rounded-md border border-border bg-background px-2 text-sm">
                    <option value="">Véhicule…</option>
                    {VEHICLE_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                  <Button variant="ghost" size="icon" className="text-destructive h-9 w-9" onClick={() => set({ team_members: prod.team_members.filter((_, x) => x !== i) })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => set({ team_members: [...(prod.team_members || []), { user_id: "", name: "", arrival_time: "", vehicle: "" }] })} data-testid="prod-tm-add">
                <Plus className="h-3 w-3 mr-1" /> Ajouter une personne
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ==== Client ==== */}
      <Card className="border-border/60 shadow-warm" data-testid="prod-client-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <UserIcon className="h-3 w-3 text-primary" /> Client — contact sur place
            </div>
            {clientContacts.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Importer depuis {event.client}</span>
                <select
                  onChange={e => { if (e.target.value !== "") importClientContact(parseInt(e.target.value)); e.target.value = ""; }}
                  defaultValue=""
                  className="h-9 rounded-md border border-primary/40 bg-primary/5 px-2 text-xs"
                  data-testid="prod-client-import"
                >
                  <option value="">— Choisir un contact ({clientContacts.length}) —</option>
                  {clientContacts.map((c, i) => (
                    <option key={i} value={i}>
                      {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Contact " + (i + 1)}
                      {c.role ? ` · ${c.role}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <OnsiteContactBlock value={prod.client_onsite} onChange={v => set({ client_onsite: v })} withMeal={false} testPrefix="prod-client" />
        </CardContent>
      </Card>

      {/* ==== Lieu ==== */}
      <Card className="border-border/60 shadow-warm" data-testid="prod-venue-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <UserIcon className="h-3 w-3 text-primary" /> Lieu — contact sur place
            </div>
          </div>
          <OnsiteContactBlock value={prod.venue_onsite} onChange={v => set({ venue_onsite: v })} withMeal={false} testPrefix="prod-venue" />
        </CardContent>
      </Card>


      {/* Feuille de route */}
      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <ListChecks className="h-3 w-3 text-primary" /> Feuille de route (heure par heure)
              <Badge variant="outline" className="rounded-full ml-2">{roadmapSorted.length} étape(s)</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-full border-primary/40 text-primary hover:bg-primary/5" onClick={prefillRoadmap} data-testid="fdr-prefill" title="Génère 'Arrivée équipe' et 'Fin montage' depuis l'horaire d'arrivée + la durée du montage">
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Préremplir
              </Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={exportPdf} data-testid="fdr-pdf">
                <Download className="h-3.5 w-3.5 mr-1" /> Exporter en PDF
              </Button>
            </div>
          </div>

          {/* Jalons de validation & envoi */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 px-3 py-2 cursor-pointer select-none" data-testid="fdr-pret-toggle">
              <Checkbox checked={fdrPret} onCheckedChange={v => setCheckpoint("fdr_pret", !!v)} data-testid="fdr-pret-checkbox" />
              <span className="text-sm font-semibold text-emerald-200">Feuille de route prête à envoyer</span>
              {fdrPret && event.checkpoints?.fdr_pret?.date && (
                <span className="text-[11px] text-emerald-200 ml-auto">Le {event.checkpoints.fdr_pret.date}</span>
              )}
            </label>
            <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 select-none ${fdrPret ? "border-emerald-500 bg-emerald-500/15 cursor-pointer" : "border-border bg-muted opacity-60 cursor-not-allowed"}`} data-testid="fdr-envoyee-toggle" title={!fdrPret ? "Cochez d'abord « prête à envoyer »" : ""}>
              <Checkbox
                checked={fdrEnvoyee}
                disabled={!fdrPret}
                onCheckedChange={v => setCheckpoint("fdr_envoyee", !!v)}
                data-testid="fdr-envoyee-checkbox"
              />
              <span className="text-sm font-bold text-emerald-950">Feuille de route envoyée au client</span>
              {fdrEnvoyee && event.checkpoints?.fdr_envoyee?.date && (
                <span className="text-[11px] text-emerald-200 ml-auto">Le {event.checkpoints.fdr_envoyee.date}</span>
              )}
            </label>
          </div>

          {/* Nouveau créneau */}
          <div className="border border-dashed border-primary/40 rounded-xl p-3 bg-primary/5">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
              <div>
                <Label className="text-[10px]">De</Label>
                <Input type="time" value={newItem.time_from} onChange={e => setNewItem(i => ({ ...i, time_from: e.target.value }))} data-testid="rm-from" />
              </div>
              <div>
                <Label className="text-[10px]">À</Label>
                <Input type="time" value={newItem.time_to} onChange={e => setNewItem(i => ({ ...i, time_to: e.target.value }))} data-testid="rm-to" />
              </div>
              <div>
                <Label className="text-[10px]">Catégorie</Label>
                <Select value={newItem.category} onValueChange={v => setNewItem(i => ({ ...i, category: v }))}>
                  <SelectTrigger data-testid="rm-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label className="text-[10px]">Action</Label>
                <Input value={newItem.action} onChange={e => setNewItem(i => ({ ...i, action: e.target.value }))} placeholder="Ex : Arrivée équipe technique" data-testid="rm-action" />
              </div>
              <div className="md:col-span-3">
                <Label className="text-[10px]">Commentaire</Label>
                <Input value={newItem.comment} onChange={e => setNewItem(i => ({ ...i, comment: e.target.value }))} placeholder="Détails" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px]">Qui</Label>
                <Input value={newItem.who} onChange={e => setNewItem(i => ({ ...i, who: e.target.value }))} placeholder="Prénom / équipe" data-testid="rm-who" />
              </div>
              <Button className="rounded-full bg-primary" onClick={addItem} data-testid="rm-add">
                <Plus className="h-4 w-4 mr-1" /> Ajouter
              </Button>
            </div>
          </div>

          {/* Timeline */}
          {roadmapSorted.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6 italic">Aucune étape planifiée</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                    <th className="py-2 px-2 font-medium">De</th>
                    <th className="py-2 px-2 font-medium">À</th>
                    <th className="py-2 px-2 font-medium">Catégorie</th>
                    <th className="py-2 px-2 font-medium">Action</th>
                    <th className="py-2 px-2 font-medium">Commentaire</th>
                    <th className="py-2 px-2 font-medium">Qui</th>
                    <th className="py-2 px-2 font-medium text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {roadmapSorted.map(r => (
                    <tr key={r.id} className="border-b border-border/40 hover:bg-muted" data-testid={`rm-row-${r.id}`}>
                      <td className="py-2 px-2 font-mono tabular-nums">{r.time_from || "—"}</td>
                      <td className="py-2 px-2 font-mono tabular-nums text-muted-foreground">{r.time_to || ""}</td>
                      <td className="py-2 px-2">
                        <Badge className={`${CATEGORY_STYLE[r.category] || ""} rounded-full text-[10px] border`}>
                          {CATEGORY_LABEL[r.category] || r.category}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 font-medium">{r.action}</td>
                      <td className="py-2 px-2 text-muted-foreground">{r.comment || ""}</td>
                      <td className="py-2 px-2 text-muted-foreground">{r.who || ""}</td>
                      <td className="py-2 px-2 text-right">
                        <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10 h-7 w-7" onClick={() => removeItem(r)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ==== Dialogue Rappel J-1 ==== */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="max-w-2xl" data-testid="prod-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-600" /> Rappel J-1 — Récap événement
            </DialogTitle>
          </DialogHeader>
          {reminderLoading && (
            <div className="py-8 text-center text-sm text-muted-foreground">Chargement de la liste des destinataires…</div>
          )}
          {!reminderLoading && reminderPreview && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="rounded-lg bg-muted p-3 text-xs">
                <div className="font-semibold text-foreground text-sm mb-1">{reminderPreview.event.name}</div>
                <div>Date : {reminderPreview.event.start_date || "—"}</div>
                {reminderPreview.event.horaire && <div>Horaires : {reminderPreview.event.horaire}</div>}
                {reminderPreview.event.address && <div>Adresse : {reminderPreview.event.address}</div>}
                {reminderPreview.event.location && <div>Lieu : {reminderPreview.event.location}</div>}
                {reminderPreview.last_sent_at && (
                  <div className="mt-2 text-amber-300 font-semibold">
                    ⚠️ Déjà envoyé le {new Date(reminderPreview.last_sent_at).toLocaleString("fr-FR")} — un nouvel envoi écrasera cette date.
                  </div>
                )}
              </div>
              {[
                { key: "aae_team", label: "Équipe AAE", icon: <Users className="h-3.5 w-3.5" /> },
                { key: "providers", label: "Prestataires", icon: <Wrench className="h-3.5 w-3.5" /> },
                { key: "artists", label: "Artistes", icon: <Sparkles className="h-3.5 w-3.5" /> },
                { key: "intermittents", label: "Intermittents", icon: <UserIcon className="h-3.5 w-3.5" /> },
              ].map(section => {
                const rows = reminderPreview[section.key] || [];
                const withEmail = rows.filter(r => r.has_email).length;
                return (
                  <div key={section.key} data-testid={`reminder-section-${section.key}`}>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
                      {section.icon} {section.label}
                      <Badge variant="secondary" className="ml-1">{withEmail}/{rows.length} email</Badge>
                    </div>
                    {rows.length === 0 && (
                      <div className="text-xs text-muted-foreground italic pl-2">Aucun</div>
                    )}
                    <ul className="text-sm space-y-1">
                      {rows.map((r, i) => (
                        <li key={i} className="flex items-center gap-2 pl-2">
                          {r.has_email ? (
                            <Mail className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-red-500 flex-shrink-0" title="Pas d'email" />
                          )}
                          <span className="font-medium">{r.name}</span>
                          {r.note && <span className="text-xs text-muted-foreground">({r.note})</span>}
                          {r.arrival_time && <span className="text-xs text-primary font-bold ml-auto">arr. {r.arrival_time}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
                <strong className="text-foreground">Envoi automatique :</strong> chaque matin à 9h, un récap est envoyé automatiquement pour les événements du lendemain.
                Utilisez le bouton ci-dessous pour un envoi immédiat (test ou renvoi manuel).
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderOpen(false)} data-testid="reminder-cancel">Fermer</Button>
            <Button
              onClick={sendReminderNow}
              disabled={reminderSending || reminderLoading}
              className="bg-primary"
              data-testid="reminder-send"
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              {reminderSending ? "Envoi…" : "Envoyer le récap maintenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
